import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invalidateInventoryQueries } from "@/lib/mutationHelpers";

/** delivery_status: ordered | delivered (default delivered) */
export type OwnerPurchaseDeliveryStatus = "ordered" | "delivered";

export interface OwnerPurchase {
  id: string;
  property_id: string;
  cleaner_id: string;
  item_id: string | null;
  amount: number;
  receipt_url: string | null;
  note: string | null;
  status: "pending_approval" | "auto_approved" | "approved" | "paid" | "rejected";
  type: "request_order" | "cleaner_purchase";
  created_at: string;
  paid_at: string | null;
  payment_failed: boolean | null;
  quantity: number;
  /** When set, reimbursement was paid immediately (autopay); show as $0.00 on invoice with "Paid on {autopay_at} (Autopay)" */
  autopay_at?: string | null;
  cleaning_task_id?: string | null;
  invoice_period_id?: string | null;
  delivery_status: OwnerPurchaseDeliveryStatus;
  proof_url: string | null;
  delivered_at: string | null;
  vendor_source: string | null;
  currency: string;
  inventory_applied: boolean;
  receipt_total_cents?: number | null;
  receipt_total_confidence?: number | null;
  receipt_total_status?: string | null;
  receipt_total_source?: string | null;
}

export interface PurchaseLineItemDetail {
  inventory_item_id: string;
  quantity: number;
  item_name: string | null;
}

export interface OwnerPurchaseWithDetails extends OwnerPurchase {
  item_name: string | null;
  cleaner_name: string | null;
  vendor_url: string | null;
  vendor_label: string | null;
  /** Multi-item purchases: line items with item name */
  line_items?: PurchaseLineItemDetail[];
}

// Fetch all purchases for a specific property (for owner)
export const usePropertyPurchases = (propertyId: string | undefined) => {
  return useQuery({
    queryKey: ["property-purchases", propertyId],
    staleTime: 1000 * 60 * 1, // 1 minute — owner should see new cleaner requests soon
    refetchOnMount: true, // Refetch when Payments tab mounts so new order requests appear
    refetchOnWindowFocus: true, // Refetch when tab focused
    placeholderData: [], // Show empty array while loading
    queryFn: async () => {
      if (!propertyId) return [];

      // Use flat select only: embed (inventory_items) can 400 for team members due to RLS on embed
      const { data: flat, error: flatError } = await supabase
        .from("cleaner_purchases")
        .select("*")
        .eq("property_id", propertyId)
        .order("created_at", { ascending: false });

      if (flatError) throw flatError;
      const purchases = (flat ?? []).map((p) => ({ ...p, inventory_items: null }));

      if (purchases.length === 0) return [];

      const cleanerIds = [...new Set(purchases.map((p) => p.cleaner_id))];
      const itemIds = [...new Set(purchases.map((p) => p.item_id).filter(Boolean))] as string[];

      const [cleanersRes, itemsRes] = await Promise.all([
        supabase.from("cleaners").select("id, name").in("id", cleanerIds),
        itemIds.length > 0
          ? supabase.from("inventory_items").select("id, item_name, vendor_url, vendor_label").in("id", itemIds)
          : { data: [] as { id: string; item_name?: string; vendor_url?: string; vendor_label?: string }[] },
      ]);

      const cleanerMap = new Map((cleanersRes.data ?? []).map((c) => [c.id, c.name]));
      const itemMap = new Map(
        (itemsRes.data ?? []).map((i) => [i.id, { item_name: i.item_name ?? null, vendor_url: i.vendor_url ?? null, vendor_label: i.vendor_label ?? null }])
      );

      const multiIds = purchases.filter((p) => p.item_id == null).map((p) => p.id);
      let lineItemsByPurchase: Record<string, PurchaseLineItemDetail[]> = {};
      if (multiIds.length > 0) {
        const { data: lineRows } = await supabase
          .from("cleaner_purchase_line_items")
          .select("purchase_id, inventory_item_id, quantity")
          .in("purchase_id", multiIds);
        if (lineRows?.length) {
          const allItemIds = [...new Set(lineRows.map((r) => r.inventory_item_id))];
          const { data: lineItemNames } = await supabase
            .from("inventory_items")
            .select("id, item_name")
            .in("id", allItemIds);
          const lineItemNameMap = new Map((lineItemNames ?? []).map((i) => [i.id, i.item_name ?? null]));
          lineItemsByPurchase = lineRows.reduce((acc, r) => {
            const list = acc[r.purchase_id] ?? [];
            list.push({
              inventory_item_id: r.inventory_item_id,
              quantity: r.quantity,
              item_name: lineItemNameMap.get(r.inventory_item_id) ?? null,
            });
            acc[r.purchase_id] = list;
            return acc;
          }, {} as Record<string, PurchaseLineItemDetail[]>);
        }
      }

      return purchases.map((p) => {
        const inv = p.inventory_items ?? (p.item_id ? itemMap.get(p.item_id as string) : null);
        const itemName = inv && typeof inv === "object" && "item_name" in inv ? (inv as { item_name?: string }).item_name ?? null : null;
        const vendorUrl = inv && typeof inv === "object" && "vendor_url" in inv ? (inv as { vendor_url?: string }).vendor_url ?? null : null;
        const vendorLabel = inv && typeof inv === "object" && "vendor_label" in inv ? (inv as { vendor_label?: string }).vendor_label ?? null : null;
        return {
          ...p,
          item_name: itemName,
          cleaner_name: cleanerMap.get(p.cleaner_id as string) || null,
          vendor_url: vendorUrl,
          vendor_label: vendorLabel,
          line_items: lineItemsByPurchase[p.id as string] ?? undefined,
        };
      }) as OwnerPurchaseWithDetails[];
    },
    enabled: !!propertyId,
    retry: false,
  });
};

// Update purchase status
export const useUpdatePurchaseStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      purchaseId,
      status,
      paidAt,
    }: {
      purchaseId: string;
      status: "pending_approval" | "auto_approved" | "approved" | "paid" | "rejected";
      paidAt?: string | null;
    }) => {
      const updateData: { status: string; paid_at?: string | null } = { status };
      if (paidAt !== undefined) {
        updateData.paid_at = paidAt;
      }

      const { data, error } = await supabase
        .from("cleaner_purchases")
        .update(updateData)
        .eq("id", purchaseId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["property-purchases"] });
      queryClient.invalidateQueries({ queryKey: ["cleaner-purchases"] });
      queryClient.invalidateQueries({ queryKey: ["all-cleaner-purchases"] });
    },
  });
};

// Approve and pay a purchase - creates Stripe PaymentIntent
export const useApproveAndPay = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (purchaseId: string) => {
      // First set status to "approved"
      const { error: updateError } = await supabase
        .from("cleaner_purchases")
        .update({ status: "approved", payment_failed: false })
        .eq("id", purchaseId);

      if (updateError) throw updateError;

      // Trigger the charge function with manual mode
      const { data, error } = await supabase.functions.invoke("charge-cleaner-purchase", {
        body: { purchase_id: purchaseId, mode: "manual" },
      });

      if (error) {
        // If charge fails, keep status as "approved" so owner can retry
        throw new Error(error.message || "Payment failed");
      }

      // Check for Stripe errors in the response
      if (data?.error) {
        throw new Error(data.error);
      }

      // Charge skipped (e.g. not delivered, no proof) – surface reason so owner can fix
      if (data?.charged === false && data?.reason) {
        throw new Error(data.reason);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["property-purchases"] });
      queryClient.invalidateQueries({ queryKey: ["cleaner-purchases"] });
      queryClient.invalidateQueries({ queryKey: ["all-cleaner-purchases"] });
      queryClient.invalidateQueries({ queryKey: ["invoice-periods"] });
      queryClient.invalidateQueries({ queryKey: ["invoice-reimbursements"] });
    },
  });
};

/** Try to run autopay charge (mode auto). Returns { charged, reason } so UI can show why it didn't pay. */
export const useTryAutopay = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (purchaseId: string): Promise<{ charged: boolean; reason?: string }> => {
      const { data, error } = await supabase.functions.invoke("charge-cleaner-purchase", {
        body: { purchase_id: purchaseId, mode: "auto" },
      });
      if (error) throw new Error(error.message || "Autopay failed");
      return {
        charged: data?.charged === true,
        reason: data?.reason,
      };
    },
    onSuccess: (_, __) => {
      queryClient.invalidateQueries({ queryKey: ["property-purchases"] });
      queryClient.invalidateQueries({ queryKey: ["cleaner-purchases"] });
      queryClient.invalidateQueries({ queryKey: ["all-cleaner-purchases"] });
      queryClient.invalidateQueries({ queryKey: ["invoice-periods"] });
      queryClient.invalidateQueries({ queryKey: ["invoice-reimbursements"] });
    },
  });
};

/** Mark a reimbursement as "Paid immediately" (autopay). It still appears on invoice as $0.00 with "Paid on {date} (Autopay)" and original amount in metadata. */
export const useMarkPurchaseAutopay = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (purchaseId: string) => {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from("cleaner_purchases")
        .update({ autopay_at: now, paid_at: now, status: "paid" })
        .eq("id", purchaseId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["property-purchases"] });
      queryClient.invalidateQueries({ queryKey: ["cleaner-purchases"] });
      queryClient.invalidateQueries({ queryKey: ["all-cleaner-purchases"] });
      queryClient.invalidateQueries({ queryKey: ["invoice-periods"] });
      queryClient.invalidateQueries({ queryKey: ["invoice-reimbursements"] });
    },
  });
};

/** Mark an ordered purchase as delivered: upload proof, then call RPC. Moves quantity from ordered_qty to in_stock_qty. */
export const useMarkPurchaseDelivered = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      purchaseId,
      proofUrl,
      propertyId,
    }: {
      purchaseId: string;
      proofUrl: string;
      propertyId: string;
    }) => {
      const { error } = await supabase.rpc("mark_cleaner_purchase_delivered", {
        p_purchase_id: purchaseId,
        p_proof_url: proofUrl,
      });
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["property-purchases"] });
      queryClient.invalidateQueries({ queryKey: ["cleaner-purchases"] });
      queryClient.invalidateQueries({ queryKey: ["all-cleaner-purchases"] });
      invalidateInventoryQueries(queryClient, variables.propertyId);
    },
  });
};
